// src/utils/triageReportPrint.js
//
// Entry point for the report's "Download PDF". The report is built client-side as an HTML string that the caller injects into a blank window
// and prints with the browser (there is no server-generated PDF). The document itself — one design for the patient's and the clinician's
// version — lives in patientReportPrint.js; this file prepares the trajectory data it needs and parses the triage JSON.

import { trajectoryCoverage, matchesMarkerName, priorDraws } from './triageUtils';
import { buildPatientPrintHtml } from './patientReportPrint';

const parseJson = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw.replace(/```json|```/g,'').trim()); } catch { return null; }
};

const buildMarkers = (claudeResult) => {
  if (!claudeResult?.markers) return [];
  return claudeResult.markers.map(m => ({
    name: m.name || '—',
    value: m.value,
    unit: m.unit || '',
    range: m.reference || m.ref || '—',
    // Map Claude's status strings to our 3 tiers: bad / warn / good
    // Claude returns: critical, elevated, borderline, normal, optimal, low, high, abnormal
    status: (['critical','very high','very low','dangerously'].some(s => String(m.status||'').toLowerCase().includes(s))
      ? 'bad'
      : (['elevated','borderline','high','low','above','below','abnormal','outside'].some(s => String(m.status||'').toLowerCase().includes(s))
        ? 'warn'
        : 'good')),
    delta: m.delta || null,
  }));
};

export const buildPrintHtml = (reportId, patient, triage, markers, missing, healthRecord, rawHistory = [], m3 = { scored: [], unscored: [], systems: {} }, readOnly = false, showOptimal = false, journey = null) => {
  const history = priorDraws(rawHistory, triage, healthRecord?.drawDate || triage.drawDate || triage.createdAt);
  // Markers shown in the Trajectory section: the ones the doctor flagged first, then the highest-signal markers, up to `limit` in total
  // (same rule as the on-screen Trajectory tab). Only markers with a value in at least two draws can have a trajectory.
  const parseHistoryMarkers = (h) => buildMarkers(parseJson(h.claudeRawResponse));
  const coverage = trajectoryCoverage(markers, history, parseHistoryMarkers);
  const pickTrajectoryMarkers = (limit) => {
    const flagged = [];
    (triage.highlightedMarkers || []).forEach(h => {
      const m = coverage.multi.find(x => matchesMarkerName(x.name, h.markerName));
      if (m && !flagged.includes(m)) flagged.push(m);
    });
    const rank = { bad: 0, warn: 1, good: 2 };
    const rest = [...coverage.multi].filter(m => !flagged.includes(m))
      .sort((a, b) => (rank[a.status] ?? 1) - (rank[b.status] ?? 1));
    return [...flagged, ...rest].slice(0, Math.max(limit, flagged.length));
  };

  // One document design for both audiences, built in patientReportPrint.js: the patient's report, or (readOnly = false) the clinician's version
  // of the same pages with the clinical sections added. Clinical Detail (Layer 11 "Navigator Actions") and the other internal sections only
  // ever appear in the staff version, as on screen.
  return buildPatientPrintHtml({
    reportId, patient, triage, markers, missing, healthRecord, history, m3, showOptimal, journey, staff: !readOnly,
    helpers: { parseHistoryMarkers, pickTrajectoryMarkers },
  });
};
